import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiMail, FiLock, FiLogIn } from 'react-icons/fi';
import { AxiosError } from 'axios';
import { useApi } from '../../hooks/useApi';
import { useAuthStore } from '../../stores';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { SimpleFormField } from '../Common/FormField';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuthStore();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  const loginMutation = useApi.useLogin({
    onSuccess: (data) => {
      if (data.token && data.user) {
        setToken(data.token);
        setUser(data.user);
        if (data.user.role === 'master_pending') {
          navigate('/register/pending');
        } else {
          navigate('/dashboard');
        }
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    loginMutation.mutate({ email, password });
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0c10] py-8 sm:py-10 px-4 sm:px-6 lg:px-8">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#246BF5]/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-28 -left-16 h-64 w-64 rounded-full bg-[#246BF5]/10 blur-3xl"
      />

      <div className="relative w-full max-w-md space-y-6 sm:space-y-8">
        <div className="text-center space-y-3">
          <div className="flex justify-center">
            <img
              src="/trade-pilot-logo.png"
              alt="Trade Pilot"
              className="h-20 w-20 md:h-24 md:w-24 object-cover"
            />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#246BF5]">
              Tradepilot AI
            </p>
            <h1 className="mt-2 text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-white">
              Trade <span className="text-[#246BF5]">Pilot</span>
            </h1>
            <p className="mt-2 text-sm text-white/55">Sign in to continue to your dashboard</p>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-[#246BF5]/25 bg-gradient-to-br from-[#246BF5]/10 via-[#111827] to-[#0f1420] px-4 py-6 sm:px-6 sm:py-8 md:px-8 shadow-[0_0_40px_rgba(36,107,245,0.08)]">
          {loginMutation.isError && (
            <ErrorDisplay
              error={
                loginMutation.error instanceof AxiosError
                  ? loginMutation.error.response?.data?.error || 'Login failed. Please try again.'
                  : loginMutation.error?.message || 'Login failed. Please try again.'
              }
              className="mb-5"
            />
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <SimpleFormField
              label="Email Address"
              name="email"
              type="email"
              icon={<FiMail className="h-5 w-5 text-[#246BF5]/70" />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
            />

            <div className="space-y-2">
              <SimpleFormField
                label="Password"
                name="password"
                type="password"
                icon={<FiLock className="h-5 w-5 text-[#246BF5]/70" />}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                showPasswordToggle={true}
              />
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-[#246BF5] hover:text-[#4f8cff] font-medium transition-colors"
                >
                  Forgot Password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#246BF5] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(36,107,245,0.35)] hover:bg-[#1d5ad4] disabled:opacity-50 transition-colors"
            >
              {loginMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <FiLogIn className="h-5 w-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-7 text-center">
            <p className="text-sm text-white/50">
              Don&apos;t have an account?{' '}
              <Link
                to="/register"
                className="font-semibold text-[#246BF5] hover:text-[#4f8cff] transition-colors"
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
